import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from '../config/api';
import { CafeSkeleton } from '../components/LoadingSkeleton';
import { usePullToRefresh } from '../hooks/usePullToRefresh';
import EmptyState from '../components/EmptyState';
import OptimizedImage from '../components/OptimizedImage';
import { FaMapMarkerAlt, FaPhone, FaCoffee } from 'react-icons/fa';
import {
  cafeHasActiveOffer,
  sortCafesForCustomerList,
  type PromotionForSort,
} from '../utils/cafeMarketingSort';
import { useAuth } from '../context/AuthContext';
import {
  campaignVisibleToAudience,
  formatPromotionGuestBadge,
  isCampaignPromotion,
  isPromotionActiveNow as isPromoLiveNow,
} from '../utils/promotionSchedule';
import { resolvePromoCafeName } from '../utils/promoCards';

interface Cafe {
  id: string;
  name: string;
  address: string;
  phone: string;
  email: string;
  image_url: string;
  marketing_budget_tier?: string;
  is_open_now?: boolean;
}

interface Promotion extends PromotionForSort {
  id: string;
  title: string;
  description?: string;
  image_url?: string;
  promotion_type?: string;
  discount_percentage?: number;
  discount_amount?: number;
  deal_type?: string | null;
  bogo_off_percent?: number | null;
  campaign_audience?: string | null;
  campaign_placement?: string | null;
  cafe_name?: string;
  valid_until: string;
  valid_from: string;
}

const CAMPAIGN_FALLBACK_IMAGES = [
  'https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?auto=format&fit=crop&w=1600&q=80',
  'https://images.unsplash.com/photo-1509042239860-f550ce710b93?auto=format&fit=crop&w=1600&q=80',
  'https://images.unsplash.com/photo-1447933601403-0c6688de566e?auto=format&fit=crop&w=1600&q=80',
];

const OFFER_FALLBACK_IMAGES = [
  'https://images.unsplash.com/photo-1521017432531-fbd92d768814?auto=format&fit=crop&w=1200&q=80',
  'https://images.unsplash.com/photo-1485808191679-5f86510681a2?auto=format&fit=crop&w=1200&q=80',
  'https://images.unsplash.com/photo-1461023058943-07fcbe16d735?auto=format&fit=crop&w=1200&q=80',
];
/** Keep in sync with MAX_HOMEPAGE_CAMPAIGN_SLOTS in server/marketing-plans.js */
const MAX_CAMPAIGN_SLIDES = 8;

function isPromotionActiveNow(promo: Promotion): boolean {
  return isPromoLiveNow({
    ...promo,
    active: promo.active === undefined ? true : promo.active,
  });
}

function promoBadgeText(promo: Promotion): string {
  return formatPromotionGuestBadge(promo);
}

function daySeed(now = new Date()): string {
  return `${now.getUTCFullYear()}-${now.getUTCMonth() + 1}-${now.getUTCDate()}`;
}

function hashString(input: string): number {
  let hash = 2166136261;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function rotateCampaignsForDay(promotions: Promotion[]): Promotion[] {
  const seed = daySeed();
  return [...promotions]
    .sort((a, b) => {
      const ap = String(a.campaign_placement || '').toLowerCase() === 'homepage' ? 1 : 0;
      const bp = String(b.campaign_placement || '').toLowerCase() === 'homepage' ? 1 : 0;
      if (bp !== ap) return bp - ap;
      const ah = hashString(`${seed}:${a.id}`);
      const bh = hashString(`${seed}:${b.id}`);
      if (bh !== ah) return bh - ah;
      return String(a.title || '').localeCompare(String(b.title || ''));
    })
    .slice(0, MAX_CAMPAIGN_SLIDES);
}

function CafeNameRow({ name }: { name: string }) {
  return (
    <div className="flex items-center gap-2 min-w-0 bg-[#fff6ee] px-3 py-2 border-t border-[#ead9c6]">
      <span
        className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#b97845] text-white text-[12px]"
        aria-hidden
      >
        ☕
      </span>
      <span className="truncate text-sm font-extrabold text-[#5a3723]">{name}</span>
    </div>
  );
}

function PromoSlideCard({
  cafeId,
  className,
  label,
  children,
}: {
  cafeId: string;
  className: string;
  label: string;
  children: React.ReactNode;
}) {
  const navigate = useNavigate();
  const startRef = useRef<{ x: number; y: number } | null>(null);
  const openedAtRef = useRef(0);
  const href = `/cafe/${cafeId}`;

  const openCafe = () => {
    if (!cafeId) return;
    const now = Date.now();
    if (now - openedAtRef.current < 500) return;
    openedAtRef.current = now;
    navigate(href);
  };

  return (
    <Link
      to={href}
      data-promo-card="1"
      aria-label={label}
      className={className}
      style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'rgba(180, 120, 70, 0.25)', cursor: 'pointer' }}
      onPointerDown={(event) => {
        startRef.current = { x: event.clientX, y: event.clientY };
      }}
      onClick={(event) => {
        event.preventDefault();
        openCafe();
      }}
      onTouchEnd={(event) => {
        const start = startRef.current;
        const touch = event.changedTouches[0];
        if (!start || !touch) return;
        if (Math.abs(touch.clientX - start.x) < 16 && Math.abs(touch.clientY - start.y) < 16) {
          event.preventDefault();
          openCafe();
        }
      }}
    >
      {children}
    </Link>
  );
}

function searchRelevanceScore(cafe: Cafe, query: string): number {
  const q = query.trim().toLowerCase();
  if (!q) return 0;
  const name = cafe.name.toLowerCase();
  const address = cafe.address.toLowerCase();
  if (name === q) return 400;
  if (name.startsWith(q)) return 300;
  if (name.includes(q)) return 220;
  if (address.startsWith(q)) return 140;
  if (address.includes(q)) return 80;
  return 0;
}

export default function Cafes() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [cafes, setCafes] = useState<Cafe[]>([]);
  const [filteredCafes, setFilteredCafes] = useState<Cafe[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('q') || '');
  const [promotionsByCafe, setPromotionsByCafe] = useState<Record<string, Promotion[]>>({});
  const [allPromotions, setAllPromotions] = useState<Promotion[]>([]);
  const [orderedCafeIds, setOrderedCafeIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadCafesAndPromotions();
  }, []);

  useEffect(() => {
    if (!user?.id) {
      setOrderedCafeIds(new Set());
      return;
    }
    axios
      .get<Array<{ cafe_id?: string }>>('/api/orders/my-orders')
      .then((res) => {
        setOrderedCafeIds(new Set((res.data || []).map((order) => String(order.cafe_id || '')).filter(Boolean)));
      })
      .catch(() => setOrderedCafeIds(new Set()));
  }, [user?.id]);

  useEffect(() => {
    filterCafes();
  }, [searchTerm, cafes, allPromotions]);

  useEffect(() => {
    setSearchTerm(searchParams.get('q') || '');
  }, [searchParams]);

  const loadCafesAndPromotions = async () => {
    await Promise.all([fetchCafes(), fetchPromotions()]);
  };

  const fetchCafes = async () => {
    try {
      setError(null);
      const response = await axios.get('/api/cafes');
      setCafes(response.data || []);
    } catch (error: any) {
      console.error('Error fetching cafes:', error);
      setError(error.response?.data?.error || error.message || 'Failed to load cafes');
    } finally {
      setLoading(false);
    }
  };

  const fetchPromotions = async () => {
    try {
      const response = await axios.get('/api/promotions');
      const promos: Promotion[] = response.data || [];
      setAllPromotions(promos);
      const grouped: Record<string, Promotion[]> = {};
      promos.forEach((p) => {
        if (!grouped[p.cafe_id]) grouped[p.cafe_id] = [];
        grouped[p.cafe_id].push(p);
      });
      setPromotionsByCafe(grouped);
    } catch (error) {
      console.error('Error fetching promotions for cafes:', error);
    }
  };

  const filterCafes = () => {
    let filtered = [...cafes];
    const query = searchTerm.trim();

    if (query) {
      filtered = filtered.filter(
        (cafe) =>
          cafe.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          cafe.address.toLowerCase().includes(searchTerm.toLowerCase())
      );

      // During search, prioritize direct text relevance first so the intended cafe appears at top.
      filtered = filtered
        .map((cafe) => ({ cafe, score: searchRelevanceScore(cafe, query) }))
        .sort((a, b) => b.score - a.score || a.cafe.name.localeCompare(b.cafe.name))
        .map((entry) => entry.cafe);
    }

    if (!query) {
      filtered = sortCafesForCustomerList(filtered, allPromotions);
    }
    setFilteredCafes(filtered);
  };

  const updateSearchInUrl = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value.trim()) {
      next.set('q', value);
    } else {
      next.delete('q');
    }
    setSearchParams(next, { replace: true });
  };

  const { isRefreshing, pullDistance, pullProgress } = usePullToRefresh({
    onRefresh: async () => {
      setLoading(true);
      await loadCafesAndPromotions();
    },
    disabled: loading,
  });

  const useLightBrand =
    typeof document !== 'undefined' && !document.documentElement.classList.contains('dark');
  const activePromotions = allPromotions.filter((p) => isPromotionActiveNow(p as Promotion));
  const campaignSlides = rotateCampaignsForDay(
    activePromotions.filter((p) => {
      const promo = p as Promotion;
      if (!isCampaignPromotion(promo)) return false;
      const hasOrdered = orderedCafeIds.has(promo.cafe_id);
      return campaignVisibleToAudience(promo, hasOrdered);
    }) as Promotion[]
  );
  const offerSlides = activePromotions.filter(
    (p) => String((p as Promotion).promotion_type || '').toLowerCase() === 'offer'
  ) as Promotion[];

  const campaignImageFor = (promo: Promotion, index: number) =>
    promo.image_url || CAMPAIGN_FALLBACK_IMAGES[index % CAMPAIGN_FALLBACK_IMAGES.length];
  const offerImageFor = (promo: Promotion, index: number) =>
    promo.image_url || OFFER_FALLBACK_IMAGES[index % OFFER_FALLBACK_IMAGES.length];

  if (loading && cafes.length === 0) {
    return (
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl md:text-4xl font-bold mb-6 text-amber-800">Available Cafes</h1>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
          <CafeSkeleton />
          <CafeSkeleton />
          <CafeSkeleton />
          <CafeSkeleton />
          <CafeSkeleton />
          <CafeSkeleton />
          <CafeSkeleton />
          <CafeSkeleton />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          <p className="font-bold">Error loading cafes</p>
          <p>{error}</p>
          <button
            type="button"
            onClick={loadCafesAndPromotions}
            className="mt-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white px-4 py-2 rounded-lg font-semibold shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-105"
            style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'rgba(0, 0, 0, 0.1)' }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (cafes.length === 0 && !loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl md:text-4xl font-bold mb-6 text-amber-800">Available Cafes</h1>
        <EmptyState
          icon="☕"
          title="No cafes available"
          message="We're sorry, but there are no cafes available at the moment. Please check back later!"
        />
      </div>
    );
  }

  // Keep ranking formula active for default view and search view.
  const displayCafes = filteredCafes;

  return (
    <div
      className={`container mx-auto px-4 py-8 pb-4 rounded-2xl ${
        useLightBrand ? 'bg-[#fbf4ec]' : ''
      }`}
    >
      {isRefreshing && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-amber-700 to-amber-600 text-white px-4 py-2 rounded-full shadow-lg z-50 flex items-center gap-2">
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
          <span>Refreshing...</span>
        </div>
      )}

      {pullDistance > 10 && !isRefreshing && (
        <div
          className="fixed top-20 left-1/2 transform -translate-x-1/2 text-amber-700 text-sm z-50"
          style={{ opacity: Math.min(pullProgress, 1) }}
        >
          Pull to refresh {pullProgress >= 1 ? '✓' : '↓'}
        </div>
      )}

      {!searchTerm.trim() && (
      <section className="mb-6 rounded-2xl border border-[#e2d4c1] bg-[#f8efe4] p-3 sm:p-4">
        {searchTerm && (
          <p className="mb-3 text-sm text-gray-600">
            Found {filteredCafes.length} cafe{filteredCafes.length !== 1 ? 's' : ''}
          </p>
        )}

        <h2 className={`text-lg font-bold mb-2 ${useLightBrand ? 'text-[#5a3723]' : 'text-amber-800'}`}>
          Featured campaigns
        </h2>
        <div
          data-h-scroll="1"
          className="overflow-x-auto snap-x snap-mandatory pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          <div className="flex gap-3">
            {campaignSlides.length > 0 ? (
              campaignSlides.map((promo, idx) => {
                const cafeName = resolvePromoCafeName(promo, cafes) || 'Open café';
                return (
                <PromoSlideCard
                  key={promo.id}
                  cafeId={promo.cafe_id}
                  label={`${cafeName}: ${promo.title}`}
                  className="snap-start shrink-0 w-[88%] sm:w-[70%] rounded-xl overflow-hidden shadow-md bg-white border border-amber-100 block text-left active:scale-[0.99] transition"
                >
                  <div className="relative">
                    <img
                      src={campaignImageFor(promo, idx)}
                      alt=""
                      className="h-36 w-full object-cover pointer-events-none"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/15 to-transparent pointer-events-none" />
                    <div className="absolute left-3 top-3 right-3 text-left pointer-events-none">
                      <span className="inline-block bg-[#b97845] text-white text-[11px] px-2 py-0.5 rounded-full font-semibold">
                        {promoBadgeText(promo) === 'Featured café' ? 'Featured café' : 'Sponsored'}
                      </span>
                      <h3 className="text-white text-lg font-bold mt-1 line-clamp-1">{promo.title}</h3>
                      {promo.description && (
                        <p className="text-white/90 text-xs mt-1 line-clamp-2 max-w-[85%]">{promo.description}</p>
                      )}
                    </div>
                  </div>
                  <CafeNameRow name={cafeName} />
                </PromoSlideCard>
                );
              })
            ) : (
              <div className="snap-start shrink-0 w-[88%] sm:w-[70%] rounded-xl overflow-hidden shadow-md bg-white border border-amber-100 relative">
                <img
                  src={CAMPAIGN_FALLBACK_IMAGES[0]}
                  alt="No active campaigns yet"
                  className="h-40 w-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/25 to-transparent pointer-events-none" />
                <div className="absolute left-3 top-3 right-3">
                  <span className="inline-block bg-[#b97845] text-white text-[11px] px-2 py-0.5 rounded-full font-semibold">
                    Campaign
                  </span>
                  <h3 className="text-white text-lg font-bold mt-2">No active campaigns yet</h3>
                  <p className="text-white/90 text-xs mt-1 max-w-[85%]">Check back soon for new updates.</p>
                </div>
              </div>
            )}
          </div>
        </div>
      <div className="mt-4">
        <h2 className={`text-lg font-bold mb-2 ${useLightBrand ? 'text-[#5a3723]' : 'text-amber-800'}`}>Offers</h2>
        <div
          data-h-scroll="1"
          className="overflow-x-auto snap-x snap-mandatory pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          <div className="flex gap-3">
            {offerSlides.length > 0 ? (
              offerSlides.map((promo, idx) => {
                const cafeName = resolvePromoCafeName(promo, cafes) || 'Open café';
                return (
                <PromoSlideCard
                  key={promo.id}
                  cafeId={promo.cafe_id}
                  label={`${cafeName}: ${promo.title}`}
                  className="snap-start shrink-0 w-[78%] sm:w-[52%] rounded-xl bg-white border border-gray-100 shadow-sm overflow-hidden block text-left active:scale-[0.99] transition"
                >
                  <div className="relative">
                    <img
                      src={offerImageFor(promo, idx)}
                      alt=""
                      className="w-full h-28 object-cover pointer-events-none"
                      loading="lazy"
                    />
                    <span className="absolute left-2 bottom-2 text-[11px] bg-[#b97845] text-white px-2 py-0.5 rounded-full font-semibold pointer-events-none">
                      {promoBadgeText(promo)}
                    </span>
                  </div>
                  <CafeNameRow name={cafeName} />
                  <div className="px-2.5 pb-2.5 text-left">
                    <h3 className="text-sm font-bold text-[#4a2e1f] line-clamp-1">{promo.title}</h3>
                    {promo.description && (
                      <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">{promo.description}</p>
                    )}
                  </div>
                </PromoSlideCard>
                );
              })
            ) : (
              <div className="snap-start shrink-0 w-[78%] sm:w-[52%] rounded-xl bg-white border border-gray-100 shadow-sm overflow-hidden">
                <div className="relative">
                  <img
                    src={OFFER_FALLBACK_IMAGES[0]}
                    alt="No active offers yet"
                    className="w-full h-28 object-cover"
                    loading="lazy"
                  />
                  <span className="absolute left-2 bottom-2 text-[11px] bg-[#b97845] text-white px-2 py-0.5 rounded-full font-semibold">
                    Offers
                  </span>
                </div>
                <div className="p-2.5">
                  <h3 className="text-sm font-bold text-[#4a2e1f]">No active offers yet</h3>
                  <p className="text-xs text-gray-600 mt-0.5">Check back soon for new offers.</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      </section>
      )}

      <h2 className={`text-lg font-bold mb-3 ${useLightBrand ? 'text-[#5a3723]' : 'text-amber-800'}`}>
        Available cafes
      </h2>

      {displayCafes.length === 0 && searchTerm ? (
        <EmptyState
          icon="🔍"
          title="No cafes found"
          message="No cafes match your search. Try a different name or address."
          actionLabel="Clear search"
          onAction={() => updateSearchInUrl('')}
        />
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4 pb-2">
          {displayCafes.map((cafe) => {
            const cafePromos = promotionsByCafe[cafe.id] || [];
            const showOfferBadge = cafeHasActiveOffer(cafePromos);
            const isClosed = cafe.is_open_now === false;

            return (
              <Link
                key={cafe.id}
                to={`/cafe/${cafe.id}`}
                className={`rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 ${
                  useLightBrand ? 'bg-[#fffaf4]' : 'bg-white'
                }`}
              >
                <div className="h-32 md:h-36 bg-gradient-to-br from-amber-200 to-amber-300 overflow-hidden relative">
                  {cafe.image_url ? (
                    <OptimizedImage
                      src={cafe.image_url}
                      alt={cafe.name}
                      className="w-full h-full object-cover"
                      placeholder="Coffee Shop"
                    />
                  ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center">
                      <div className="text-center">
                        <FaCoffee className="text-5xl text-amber-700 mb-2 mx-auto" />
                        <span className="text-xs text-amber-700 font-medium">Coffee Shop</span>
                      </div>
                    </div>
                  )}
                </div>
                <div className="p-2 md:p-3">
                  <div className="flex items-start justify-between mb-1 gap-2">
                    <div className="flex-1">
                      <h3 className={`text-sm md:text-base font-bold line-clamp-1 ${useLightBrand ? 'text-[#5a3723]' : 'text-amber-800'}`}>
                        {cafe.name}
                      </h3>
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      {isClosed && (
                        <span className="bg-gray-600 text-white px-1.5 py-0.5 rounded-full text-[10px] font-semibold">
                          Closed
                        </span>
                      )}
                      {showOfferBadge && (
                        <span className={`text-white px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${useLightBrand ? 'bg-[#b97845]' : 'bg-red-500'}`}>
                          Offer
                        </span>
                      )}
                    </div>
                  </div>
                  <p className="text-gray-600 text-xs line-clamp-1 mb-1 flex items-center gap-1">
                    <FaMapMarkerAlt className="text-amber-600" />
                    {cafe.address}
                  </p>
                  <p className="text-gray-500 text-xs flex items-center gap-1">
                    <FaPhone className="text-amber-600" />
                    {cafe.phone}
                  </p>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
